import React, { useRef, useReducer } from 'react';
import { initialState, reducer } from 'src/store/panAndZoom/panAndZoomReducer';
import {
  pan,
  startPan,
  zoom,
  reset,
} from 'src/store/panAndZoom/pandAndZoomActions';
import { PanAndZoomStoreState } from 'src/store/panAndZoom/panAndZoomStore.types';

export interface UsePanAndZoomHookType extends PanAndZoomStoreState {
  worldMapContainerRef: React.RefObject<HTMLDivElement>;
  worldMapSVGContainerRef: React.RefObject<HTMLDivElement>;
  onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
  onWheel: (event: WheelEvent) => void;
  onReset: () => void;
  onZoom: (isZoomIn: boolean) => void;
}

export const usePanAndZoom = (): UsePanAndZoomHookType => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const worldMapContainerRef = useRef<HTMLDivElement>(null);
  const worldMapSVGContainerRef = useRef<HTMLDivElement>(null);

  const onMouseMoveInWindow = (event: MouseEvent): void => {
    event.preventDefault();
    if (worldMapSVGContainerRef.current) {
      dispatch(
        pan(event.clientX, event.clientY, worldMapSVGContainerRef.current)
      );
    }
  };

  const onMouseUpInWindow = (): void => {
    window.removeEventListener('mouseup', onMouseUpInWindow);
    window.removeEventListener('mousemove', onMouseMoveInWindow);
  };

  const onMouseDown = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (worldMapSVGContainerRef.current) {
      dispatch(
        startPan(event.clientX, event.clientY, worldMapSVGContainerRef.current)
      );
      window.addEventListener('mouseup', onMouseUpInWindow);
      window.addEventListener('mousemove', onMouseMoveInWindow);
    }
  };

  const onWheel = (event: WheelEvent): void => {
    if (event.deltaY !== 0 && worldMapContainerRef.current) {
      const deltaY = event.deltaY / Math.abs(event.deltaY);
      const clientX = event.clientX;
      const clientY = event.clientY;
      const containerRect =
        worldMapContainerRef.current.getBoundingClientRect();
      dispatch(zoom(deltaY, clientX, clientY, containerRect));
    }
  };

  const onZoom = (isZoomIn: boolean): void => {
    if (worldMapContainerRef.current) {
      const containerRect =
        worldMapContainerRef.current.getBoundingClientRect();
      // Create synthetic wheel event that zooms into the middle of the map
      const deltaY = isZoomIn ? -1 : 1;
      const clientX = containerRect.left + containerRect.width / 2;
      const clientY = containerRect.top + containerRect.height / 2;

      dispatch(zoom(deltaY, clientX, clientY, containerRect));
    }
  };

  const onReset = (): void => {
    dispatch(reset());
  };

  return {
    ...state,
    worldMapContainerRef,
    worldMapSVGContainerRef,
    onMouseDown,
    onWheel,
    onReset,
    onZoom,
  };
};
